import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import { useState } from 'react'
import { getCustomMcpRegistrationError } from '../utils/custom-mcp-registration-error'
import { type CustomMcpFormValues, customMcpOAuthFields } from './custom-mcp-form.config'

interface CustomMcpOAuthSettingsProps {
  form: GenericFormType<CustomMcpFormValues>
  error: Error | null
  submittedValues?: CustomMcpFormValues
}

export const CustomMcpOAuthSettings = ({
  form,
  error,
  submittedValues,
}: CustomMcpOAuthSettingsProps) => {
  const values = form.useStore((state) => state.values)
  const [expanded, setExpanded] = useState(false)
  const { status, body } = getCustomMcpRegistrationError(error)
  const hint =
    status === 400 &&
    submittedValues?.authType === 'oauth' &&
    submittedValues.url.trim() === values.url.trim()
      ? body
      : undefined
  const [previousHint, setPreviousHint] = useState(hint)

  if (previousHint !== hint) {
    setPreviousHint(hint)
    if (hint?.code === 'client_id_required') setExpanded(true)
  }

  if (values.authType !== 'oauth') return null
  const requiresClient = hint?.code === 'client_id_required'

  return (
    <div className="grid gap-3">
      {(requiresClient || hint?.code === 'no_auth_required') && (
        <p role="alert" className="text-s-400 text-secondary">
          {hint.error}
        </p>
      )}
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <Button type="button" variant="ghost" size="sm" data-testid="button-mcp-oauth-advanced">
            Advanced settings
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 grid gap-4">
          {requiresClient && hint.redirect_uri && (
            <div className="grid gap-2 rounded-md border border-st2 bg-bg2 p-3">
              <p className="text-xs-400 text-secondary">
                Register this redirect URI with your OAuth provider.
              </p>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 break-all text-xs-400">{hint.redirect_uri}</code>
                <ClipboardCopyButton
                  textToCopy={hint.redirect_uri}
                  aria-label="Copy redirect URI"
                  tooltipText="Copy redirect URI"
                  data-testid="button-copy-mcp-redirect-uri"
                />
              </div>
            </div>
          )}
          <FieldsRenderer fields={customMcpOAuthFields} form={form} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
